import { atom } from 'jotai';
import type { LngLat, SearchLocations } from './coordinate-utils';

// Pins and the card are app-wide, so every read and write goes to `appStore` from
// components/map: a map under a page's own `<Provider>` would otherwise get an empty copy.

export interface CoordinatePin {
  id: number;
  lngLat: LngLat;
  /** Marker colour; any CSS colour the shared colour picker produces. */
  color: string;
  /** The well or place it was dropped on, shown in place of its coordinates. */
  label?: string;
}

/**
 * Default pin colours from the Stronghold brand palette, ordered so neighbouring pins
 * stay distinguishable: navy, mid blue, light blue, slate, sky, steel. Cycles.
 */
const PIN_COLORS = [
  '#15293e',
  '#44729e',
  '#79aace',
  '#404654',
  '#b1d0da',
  '#2f4e6e',
];

/** Whether the Coordinates card is open. Map clicks drop pins while it is. */
export const coordinatesOpenAtom = atom(false);

/** Where the cursor is over the map; the last spot once it leaves. */
export const coordinatesCursorAtom = atom<LngLat | null>(null);

export const coordinatePinsAtom = atom<CoordinatePin[]>([]);

/** The pin whose popup is showing. */
export const openPinIdAtom = atom<number | null>(null);

/**
 * The app shell's well and place search for the Location field; without one the field
 * takes coordinates only. Boxed, since Jotai would call a bare function as an updater.
 */
export const locationSearchAtom = atom<{ search: SearchLocations } | null>(
  null
);

const pinSeqAtom = atom(1);

/** Every map's Coordinates button, in the order they mounted. */
export const cardHostsAtom = atom<symbol[]>([]);

export const addCardHostAtom = atom(null, (get, set, host: symbol) => {
  set(cardHostsAtom, [...get(cardHostsAtom), host]);
});

/** Drops a map's button, and closes the card once no map shows one. Pins stay. */
export const removeCardHostAtom = atom(null, (get, set, host: symbol) => {
  const remaining = get(cardHostsAtom).filter((other) => other !== host);
  set(cardHostsAtom, remaining);
  if (remaining.length === 0) set(coordinatesOpenAtom, false);
});

/** The button last used to open the card. */
const cardOpenedFromAtom = atom<symbol | null>(null);

/**
 * The one button that draws the card, so two maps on screen don't show two. It's the
 * button that opened it, or once that map has gone, the first map still showing one.
 */
export const cardHostAtom = atom((get) => {
  const hosts = get(cardHostsAtom);
  const openedFrom = get(cardOpenedFromAtom);
  return openedFrom && hosts.includes(openedFrom)
    ? openedFrom
    : (hosts[0] ?? null);
});

/** Opens the card beside the given map's button. */
export const openCoordinatesAtom = atom(null, (_get, set, host: symbol) => {
  set(cardOpenedFromAtom, host);
  set(coordinatesOpenAtom, true);
});

export const closeCoordinatesAtom = atom(null, (_get, set) => {
  set(coordinatesOpenAtom, false);
});

/** Adds a pin, labelled if it marks a well or place, and returns its id. */
export const addPinAtom = atom(
  null,
  (get, set, lngLat: LngLat, label?: string): number => {
    const id = get(pinSeqAtom);
    set(pinSeqAtom, id + 1);
    set(coordinatePinsAtom, [
      ...get(coordinatePinsAtom),
      { id, lngLat, color: PIN_COLORS[(id - 1) % PIN_COLORS.length], label },
    ]);
    return id;
  }
);

export const removePinAtom = atom(null, (get, set, id: number) => {
  set(
    coordinatePinsAtom,
    get(coordinatePinsAtom).filter((pin) => pin.id !== id)
  );
  if (get(openPinIdAtom) === id) set(openPinIdAtom, null);
});

export const movePinAtom = atom(
  null,
  (get, set, { id, lngLat }: { id: number; lngLat: LngLat }) => {
    set(
      coordinatePinsAtom,
      get(coordinatePinsAtom).map((pin) =>
        // Moved off the well or place it was named for, it's just coordinates.
        pin.id === id ? { ...pin, lngLat, label: undefined } : pin
      )
    );
  }
);

export const recolorPinAtom = atom(
  null,
  (get, set, { id, color }: { id: number; color: string }) => {
    set(
      coordinatePinsAtom,
      get(coordinatePinsAtom).map((pin) =>
        pin.id === id ? { ...pin, color } : pin
      )
    );
  }
);

export const clearPinsAtom = atom(null, (_get, set) => {
  set(coordinatePinsAtom, []);
  set(openPinIdAtom, null);
});
